import { useEffect } from "react";

const isMacPlatform = () =>
  typeof navigator !== "undefined" &&
  /Mac|iPod|iPhone|iPad/.test(navigator.platform);

/**
 * Opens Genie with ⌘K (Ctrl+K elsewhere), in either app: the app says how
 * (`onOpen`, stable across renders) and when the shortcut applies
 * (`isEnabled`). DEDUPE-2 C3: admin's copy took the callback, layout's
 * dispatched its slice; the hook now imports no slice.
 *
 * Opens rather than toggles, deliberately: the shortcut is the way in, and a
 * toggle closes an answer the user is reading if they reach for it twice. It is
 * also idempotent, so nothing breaks if a second listener ever exists.
 *
 * A document listener is the only way to catch a key pressed anywhere on the
 * page; there is no render-time equivalent.
 */
export const useOpenGenieShortcut = ({
  isEnabled = true,
  onOpen,
}: {
  isEnabled?: boolean;
  onOpen: () => void;
}) => {
  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      const hasModifier = isMacPlatform() ? event.metaKey : event.ctrlKey;
      if (!hasModifier || event.altKey || event.shiftKey) {
        return;
      }
      if (event.key.toLowerCase() !== "k") {
        return;
      }
      // The browser's own ⌘K (search bar focus) would otherwise win.
      event.preventDefault();
      onOpen();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isEnabled, onOpen]);
};
